import { useRef, useState } from 'react';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { dateTime } from '@/lib/format';
import { exportWorkbook, fileName, printScreen, type Sheet } from '@/lib/export';
import { Button } from '@/components/ui/Button';

interface ExportBarProps {
  /** What the document is called, on paper and in the workbook. */
  title: string;
  /** The period, site or filter it shows. */
  subtitle?: string;
  /** The file name's stem; the date is added. */
  file: string;
  /** The workbook's sheets, built when Excel is asked for. */
  sheets: () => Sheet[] | Promise<Sheet[]>;
  /** Wide tables print across the page. */
  landscape?: boolean;
  /** Nothing loaded yet. */
  disabled?: boolean;
  /** Print only the drawer this bar sits in, not the page behind it. */
  drawer?: boolean;
}

/**
 * Export PDF and Export Excel for a screen (0040). On paper the buttons
 * vanish and a heading takes their place: what it is, for which period,
 * and when and by whom it was printed.
 */
export function ExportBar({ title, subtitle, file, sheets, landscape = false, disabled = false, drawer = false }: ExportBarProps) {
  const ref = useRef<HTMLDivElement>(null);
  const who = useAuthStore((s) => s.profile?.full_name ?? null);
  const [busy, setBusy] = useState<'pdf' | 'xlsx' | null>(null);
  const [printedAt, setPrintedAt] = useState<string>(() => new Date().toISOString());

  async function pdf() {
    setBusy('pdf');
    setPrintedAt(new Date().toISOString());
    try {
      // Let the heading take the new time before a drawer is copied.
      await new Promise((r) => setTimeout(r, 0));
      await printScreen(fileName(file), landscape, drawer ? ref.current?.closest<HTMLElement>('.drawer') : null);
    } finally {
      setBusy(null);
    }
  }

  async function xlsx() {
    setBusy('xlsx');
    try {
      const built = await sheets();
      await exportWorkbook(fileName(file), built, { title, subtitle, by: who });
      toast.success('Workbook downloaded');
    } catch (err) {
      toast.error(`Could not build the workbook: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="export-bar no-print" ref={ref}>
        <Button variant="secondary" disabled={disabled || busy !== null} loading={busy === 'pdf'}
          onClick={() => void pdf()} title="A print layout of this screen; choose Save as PDF">
          Export PDF
        </Button>
        <Button variant="secondary" disabled={disabled || busy !== null} loading={busy === 'xlsx'}
          onClick={() => void xlsx()} title="The figures on this screen as an Excel workbook">
          Export Excel
        </Button>
      </div>
      <div className="print-only print-header">
        <div className="print-header__title">{title}</div>
        {subtitle && <div className="print-header__sub">{subtitle}</div>}
        <div className="print-header__meta">
          Suremix Operations Platform · printed {dateTime(printedAt)}{who ? ` by ${who}` : ''}
        </div>
      </div>
    </>
  );
}
